import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "previewImage", "previewHeadline", "previewBody",
    "imageGrid", "imageThumb",
    "headline", "headlineInput", "charCount",
    "selectedImageInput",
    "budgetTile", "durationTile",
    "budgetInput", "durationInput",
    "adBudget", "platformFee", "total",
    "submitBtn"
  ]

  static values = {
    platformFeeRate: Number,
    selectedBudget: { type: Number, default: 0 },
    selectedDuration: { type: Number, default: 0 },
    actionUrl: String
  }

  connect() {
    if (this.hasImageThumbTarget) {
      this.imageThumbTargets[0]?.setAttribute("data-selected", "true")
      this.imageThumbTargets[0]?.classList.add("border-accent")
    }
    this.syncHeadlineInput()
    this.updatePricing()
    if (this.hasHeadlineTarget && this.hasCharCountTarget) {
      this.charCountTarget.textContent = 60 - this.headlineTarget.value.length
    }
  }

  // Budget selection
  selectBudget(event) {
    const tile = event.currentTarget
    const cents = parseInt(tile.dataset.cents, 10)
    this.selectedBudgetValue = cents
    if (this.hasBudgetInputTarget) this.budgetInputTarget.value = cents
    this.highlightTiles(this.budgetTileTargets, tile)
    this.updatePricing()
  }

  // Duration selection
  selectDuration(event) {
    const tile = event.currentTarget
    const days = parseInt(tile.dataset.days, 10)
    this.selectedDurationValue = days
    if (this.hasDurationInputTarget) this.durationInputTarget.value = days
    this.highlightTiles(this.durationTileTargets, tile)
    this.updatePricing()
  }

  // Image picker
  openImagePicker(event) {
    event?.preventDefault()
    if (this.hasImageGridTarget) {
      this.imageGridTarget.classList.toggle("hidden")
    }
  }

  selectImage(event) {
    const btn = event.currentTarget
    const url = btn.dataset.url

    if (this.hasSelectedImageInputTarget) {
      this.selectedImageInputTarget.value = url
    }

    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.src = url
      this.previewImageTarget.style.display = "block"
    }

    // Highlight selected thumbnail
    this.imageThumbTargets.forEach(thumb => {
      const isSelected = thumb === btn
      thumb.setAttribute("data-selected", isSelected ? "true" : "false")
      thumb.classList.toggle("border-accent", isSelected)
      thumb.classList.toggle("border-transparent", !isSelected)
    })

    // Close picker
    if (this.hasImageGridTarget) {
      this.imageGridTarget.classList.add("hidden")
    }
  }

  // Headline editing
  updateHeadline(event) {
    const value = event.target.value
    if (this.hasPreviewHeadlineTarget) {
      this.previewHeadlineTarget.textContent = value || "—"
    }
    this.syncHeadlineInput()

    // Character countdown
    if (this.hasCharCountTarget) {
      this.charCountTarget.textContent = 60 - value.length
    }
  }

  syncHeadlineInput() {
    if (this.hasHeadlineTarget && this.hasHeadlineInputTarget) {
      this.headlineInputTarget.value = this.headlineTarget.value
    }
  }

  // Pricing
  updatePricing() {
    const budget = this.selectedBudgetValue
    const days = this.selectedDurationValue
    const totalBudget = budget * days
    const fee = Math.ceil(totalBudget * (this.platformFeeRateValue || 0.2))
    const charge = totalBudget + fee

    const fmt = (cents) => cents > 0 ? `${Math.round(cents / 100)} zł` : "—"

    if (this.hasAdBudgetTarget) this.adBudgetTarget.textContent = fmt(totalBudget)
    if (this.hasPlatformFeeTarget) this.platformFeeTarget.textContent = fmt(fee)
    if (this.hasTotalTarget) this.totalTarget.textContent = fmt(charge)

    // Enable submit only when both budget and duration are selected
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = budget === 0 || days === 0
    }
  }

  async submit(event) {
    event?.preventDefault()
    const btn = this.hasSubmitBtnTarget ? this.submitBtnTarget : null
    if (btn) btn.disabled = true

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const body = new URLSearchParams({
      "ad_campaign[selected_image_url]": this.hasSelectedImageInputTarget ? this.selectedImageInputTarget.value : "",
      "ad_campaign[budget_daily_cents]": this.selectedBudgetValue,
      "ad_campaign[duration_days]": this.selectedDurationValue,
      "ad_campaign[headline]": this.hasHeadlineInputTarget ? this.headlineInputTarget.value : "",
    })

    try {
      const response = await fetch(this.actionUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest",
        },
        body: body.toString(),
        credentials: "same-origin",
      })
      if (response.ok || response.redirected) {
        window.location.reload()
      } else {
        if (btn) btn.disabled = false
        alert("Something went wrong. Please try again.")
      }
    } catch {
      if (btn) btn.disabled = false
      alert("Network error. Please try again.")
    }
  }

  // Highlight active tile, clear others
  highlightTiles(tiles, activeTile) {
    tiles.forEach(tile => {
      const isActive = tile === activeTile
      tile.setAttribute("data-active", isActive ? "true" : "false")
    })
  }
}
